import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import Image from '../../../Core/Image';
import Container from '../../../Core/Container';

// * Background wrapper
export const Wrapper = styled.div`
  background-color: ${({ colourOverrideBackground }) =>
    colourOverrideBackground || themeGet('colors.colors.neutral_1')};
  overflow: hidden;
`;

// * Grid Container (Applieds padding, centering, bg circle)
export const StyledContainer = styled(Container)`
  padding: 45px 0 0;

  display: flex;
  flex-direction: column;

  // * BG circle
  &::after {
    content: '';
    height: 210px;
    width: 408px;
    background-image: url(${({ background }) => background});
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;

    position: absolute;
    bottom: 0;
    right: -75px;

    width: calc(100% + 100px);
    margin: 0 -50px;
    height: 100%;
    max-height: 300px;
    background-size: contain;
    background-position: bottom;
    left: auto;
    right: auto;
  }

  &.bottom-circle.reduced-width,
  &.top-circle.reduced-width,
  &.null-circle.reduced-width {
    &::after {
      width: calc(100% + 100px);
      margin: 0 -50px;
      height: 100%;
      max-height: 300px;
      background-size: contain;
      background-position: bottom;
      left: auto;
      right: auto;
    }

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      &::after {
        height: 90%;
        width: 70%;
        right: -30%;
        max-height: 100%;
        max-width: 100%;
        margin: 0;
        background-position: bottom;
        background-size: contain;
      }
    }

    @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
      &::after {
        height: 90%;
        width: 60%;
        right: -20%;
        max-height: 100%;
        max-width: 100%;

        background-position: bottom;
        background-size: contain;
      }
    }
  }

  &.bottom-circle.Inside,
  &.top-circle.Inside,
  &.null-circle.Inside {
    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      &::after {
        right: -20%;
      }
    }

    @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
      &::after {
        right: -10%;
      }
    }
  }

  &.right-circle {
    &::after {
      height: 100%;
      width: calc(100% + 60px);
      margin: 0 -30px;
      background-position: bottom;
      background-size: contain;
      right: auto;
      background-image: url(${({ mobileImg, background }) =>
        mobileImg || background});
    }
  }

  @media only screen and (max-width: 440px) {
    &.right-circle {
      &::after {
        max-height: 250px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 45px 0 0;

    &.reverse {
      flex-direction: row-reverse;
    }

    // * BG circle
    &::after {
      position: absolute;
      bottom: 0;
      height: 90%;
      max-height: 100%;
      max-width: 100%;

      background-position: bottom;
      background-size: contain;

      width: 60%;
      right: -20%;
    }

    &.right-circle {
      &::after {
        background-size: contain;
        background-position: bottom;
        background-image: url(${({ background }) => background});
        max-height: 100%;
        left: 100%;
        transform: translateX(-48%);
        width: 800px;

        margin: 0;
        left: 55%;
        right: auto;
        transform: none;
        min-width: 750px;
        max-width: initial;
        width: 100%;
        height: 100%;

        width: 750px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 70px 0 0;

    // * BG circle
    &::after {
      position: absolute;
      bottom: 0;
      height: 90%;
      max-height: 100%;
      max-width: 100%;

      background-position: bottom;
      background-size: contain;

      width: 60%;
      right: -20%;
    }

    &.right-circle {
      &::after {
        left: 100%;
        width: calc(100% + 90px);
        transform: translateX(-50%);
      }
    }
  }

  @media only screen and (min-width: 1080px) {
    &.right-circle {
      &::after {
        width: 100%;
        transform: translateX(-55%);
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    padding-left: 45px;

    // * BG circle
    &::after {
      width: 55%;
    }

    &.right-circle {
      &::after {
        left: 100%;
        transform: translateX(-55%);
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.max')}) {
    padding-left: 90px;

    &.right-circle {
      &::after {
        transform: none;
        width: 1440px;
        left: auto;
        right: -570px;
      }
    }
  }
`;

// * Container for the title, copy, and button
export const Content = styled.div`
  width: 100%;
  max-width: 764px;
  padding-bottom: 75px;
  z-index: 1;

  > a {
    margin-right: 10px;
    margin-bottom: 10px;
    width: 80%;
  }

  /* Hero title */
  > h1,
  > h2 {
    color: ${({ heroHeadingColourOverride }) =>
      heroHeadingColourOverride || themeGet('colors.text-primary')};
    margin-bottom: 30px;

    span &.script,
    &.prodigy-font,
    span.script,
    span.prodigy-font {
      line-height: 35px;
      display: block;
      /* check for colour override before applying preferred styles to prodigy sans, if custom colour override is being used default to primary instead of secondary color */
      color: ${({ colourOverrideBackground }) =>
        themeGet(
          colourOverrideBackground ? 'colors.primary' : 'colors.secondary'
        )};
      font-size: 60px;

      /* adjust font size for hero on under the same conditions */
      @media only screen and (max-width: ${themeGet('breakpoints.md')}) {
        font-size: 30px;
      }
    }
  }

  /* Body Copy */
  p {
    color: ${({ heroSubheadingColourOverride }) =>
      heroSubheadingColourOverride || themeGet('colors.text-primary')};
    margin-bottom: 40px;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.41px;
    line-height: 24px;
  }

  ${StyledContainer}.reduced-width & {
    max-width: 620px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding-bottom: 54px;

    > h1,
    > h2 {
      span {
        line-height: 46px;
      }
    }

    p {
      max-width: 400px;
    }

    ${StyledContainer}.reduced-width & {
      min-width: 350px;
      padding-right: 35px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding-bottom: 80px;

    > a {
      width: auto;
    }

    > h1,
    > h2 {
      margin-bottom: 50px;
    }

    p {
      margin-bottom: 60px;
      max-width: 500px;
    }

    ${StyledContainer}.reduced-width & {
      margin: auto 0;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    p {
      max-width: 100%;
    }
  }
`;

// * Hero image
export const Img = styled(Image)`
  /* Default Styling */
  z-index: 1;
  height: 100%;
  object-fit: contain;
  max-width: initial;
  max-height: 300px;
  width: calc(100% + 60px);
  margin: 0 -30px;
  
  /* Default Styling for 50/50 */
  ${StyledContainer}.reduced-width & {
    max-width: initial;
    max-height: 300px;
    width: calc(100% + 60px);
    margin: 0 -30px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    /* Default Styling */
    align-self: flex-end;
    height: auto;
    margin: 0;
    width: 35%;
    max-height: 385px;


    /* Default Styling for 50/50 */
    ${StyledContainer}.reduced-width & {
      width: 100%;
      margin: 0;
      width: 85%;
      max-height: 385px;
      margin-right: -10%;
    }

    /* Styling for image Inside grid option */
    ${StyledContainer}.inside &, ${StyledContainer}.Inside & {
      margin-right: 0 !important;
      /* width: 50% !important; */
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    height: 90%;
    width: 25%;
    max-width: 100%;

    max-height: 395px;
    min-width: 280px;

    /* Default Styling for 50/50 */
    ${StyledContainer}.reduced-width & {
      height: 90%;
      width: 50%;
      max-height: 512px;
      max-width: 100%;
      min-width: auto;
    }

    /* Styling for image Inside grid option */
    ${StyledContainer}.inside &, ${StyledContainer}.Inside & {
      width: 50% !important;
      max-height: 500px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.max')}) {

    /* Default Styling for 50/50 */
    ${StyledContainer}.reduced-width & {
      margin-right: -10%;
      max-width: 659px;
      width: 60%;
    }
  }
`;

// * Container for bottom content under CTA
export const BottomContent = styled.div`
  p {
    margin-bottom: 0px;
  }
`;
